import React from 'react';
import styled from 'styled-components';
import { Flex, Title, Text, Button, Link, Group, Img } from '@logz-ui/styleguide';
import { ServiceListSubjects } from '@logz-pkg/enums';
import { analyticsService } from 'services/analytics/analytics.service';
import liveModePreviewSrc from 'static/welcome/livetail-migrate.png';

const LIVE_MODE_URL = '/360/explore?mode=LIVE&from=now&to=now';
const CLASSIC_LIVETAIL_URL = '#/dashboard/live-tail?skipMigration=true';
const DOCS_URL = 'https://docs.logz.io/docs/open360/open360-welcome/';

const listItems: [string, string][] = [
  ['Enhanced Performance', 'Experience faster, more reliable real-time log streaming with a modern interface.'],
  [
    'Unified Workflow',
    'Access live logs directly within Explore, using the same powerful filtering and search capabilities you already know.',
  ],
  [
    'Always evolving',
    'New ways to connect with other Logz.io features and integrate seamlessly with your existing workflows.',
  ],
];

const StyledBackground = styled(Flex)`
  position: relative;
  width: 5%;

  &:before {
    clip-path: polygon(0 0, 50% 0, 100% 100%, 0 100%);
    background: #fdff37;
    content: '';
    inset: 0;
    position: absolute;
    z-index: 1;
  }
`;

const StyledVideoBackground = styled(Flex)`
  position: relative;
  width: 40%;
  padding-left: 75px;

  &:before {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 75px 100%);
    background: #002e42;
    content: '';
    inset: 0;
    position: absolute;
    z-index: 1;
  }
`;

const ImageContainer = styled(Flex)`
  width: 500px;
  height: 290px;
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(-50px, -50%);
  z-index: 2;
  border: 8px solid ${({ theme }) => theme.colors.gray[0]};
  background: ${({ theme }) => theme.colors.gray[0]};
  border-radius: 13px;
  overflow: hidden;
  align-items: center;
  justify-content: center;
`;

const PreviewImage = styled(Img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledList = styled.ol`
  padding-left: 16px;
  list-style: decimal;
`;

const StyledItem = styled.li`
  margin-bottom: 16px;

  &::marker {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.gray[900]};
    font-weight: 700;
  }
`;

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.colors.royalBlue[400]};
`;

const handleTryLiveMode = async (): Promise<void> => {
  await analyticsService.capture('Live tail migration', 'Clicked Try Live Mode');
  window.location.href = LIVE_MODE_URL;
};

const handleStayOnClassic = async (): Promise<void> => {
  await analyticsService.capture('Live tail migration', 'Clicked Stay on Classic Live Tail');
  window.location.href = CLASSIC_LIVETAIL_URL;
};

export const LivetailMigrationPage: React.FC = () => {
  return (
    <Flex width={'100%'} height={'100%'} flexDirection={'row'} subject={ServiceListSubjects.FeatureEmptyState}>
      <StyledBackground />
      <Flex flex={1} alignItems={'center'} justifyContent={'space-around'}>
        <Flex flexDirection={'column'} width={'75%'} pr={'100px'}>
          <Title mb={2} size={24}>
            Live Tail is Getting a Facelift
          </Title>
          <Text>
            Live Tail is moving to Open 360 AI with an enhanced experience. <br />
          </Text>
          <Text noTextWrap weight={700} mb={4}>
            Soon, real-time log streaming will only be available in Open 360 AI.
          </Text>
          <Flex flexDirection={'column'} width={'100%'}>
            <StyledList>
              {listItems.map(([title, description], index) => (
                <StyledItem key={index}>
                  <Flex>
                    <Text color={'gray.900'} weight={700}>
                      {title}
                    </Text>
                  </Flex>
                  <Flex>
                    <Text color={'gray.900'}>{description}</Text>
                  </Flex>
                </StyledItem>
              ))}
            </StyledList>
            <Group alignItems={'start'} gap={2} pl={4} vertical>
              <Button size={'l'} onClick={handleTryLiveMode} endIcon={'arrow-right-regular'}>
                Try Live Mode in Open 360 AI
              </Button>
              <Button size={'l'} variant={'tertiary'} onClick={handleStayOnClassic}>
                Stay on Classic Live Tail
              </Button>
              <Flex flexDirection={'column'}>
                <StyledText size={12}>Learn more about Open 360 AI</StyledText>
                <Link href={DOCS_URL} size={12}>
                  {DOCS_URL}
                </Link>
              </Flex>
            </Group>
          </Flex>
        </Flex>
      </Flex>
      <StyledVideoBackground>
        <ImageContainer>
          <PreviewImage src={liveModePreviewSrc} alt={'Live Mode Preview'} />
        </ImageContainer>
      </StyledVideoBackground>
    </Flex>
  );
};
